import { motion } from "framer-motion";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { SEOHead } from "@/components/SEOHead";

const LicensePage = () => {
  return (
    <div className="min-h-screen bg-[#080808]" data-testid="license-page">
      <SEOHead 
        title="Licensed Authority - OnPoint Authority Systems"
        description="Authority is granted contractually, governed operationally, and enforced continuously."
        url="https://onpointauthority.com/license"
      />
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-44 md:pb-24 px-6 md:px-12" data-testid="license-hero">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-light tracking-tight leading-[0.95] text-foreground mb-8">
              Licensed<br />
              <span className="text-primary">Authority</span>
            </h1>
            <p className="text-xl md:text-2xl font-light leading-relaxed text-muted-foreground">
              Authority is granted contractually, governed operationally, and enforced continuously.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What A License Is */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="what-license-is">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-8">
              What A License Is
            </p>
            <div className="space-y-6 text-lg font-light leading-relaxed text-muted-foreground">
              <p>
                A license within OnPoint Authority Systems is not permission to operate freely.
              </p>
              <p className="text-foreground">
                It is a contractual grant of defined authority, subject to:
              </p>
              <ul className="space-y-3 mt-6">
                {[
                  "Scope limitations",
                  "Audit requirements",
                  "Enforcement actions",
                  "Revocation rights",
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3 text-foreground">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2.5" />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="text-foreground pt-4">
                The license governs what may be decided, how it is executed, and what happens if standards are breached.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What A License Is Not */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#050505]" data-testid="what-license-is-not">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-red-400 mb-8">
              What A License Is Not
            </p>
            <ul className="space-y-4">
              {[
                "Not ownership of IP",
                "Not permanent access",
                "Not transferable without approval",
                "Not negotiable after issuance",
              ].map((item, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="flex items-start gap-3 text-lg text-foreground"
                >
                  <div className="w-1.5 h-1.5 bg-red-400 rounded-full mt-3" />
                  {item}
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* License Enforcement */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="license-enforcement">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-8">
              License Enforcement
            </p>
            <p className="text-lg text-foreground mb-6">
              All licenses are monitored.
            </p>
            <p className="text-muted-foreground mb-4">Enforcement includes:</p>
            <ul className="space-y-3">
              {[
                "Scheduled audits",
                "Event-triggered reviews",
                "Mandatory remediation",
                "Conditional status or revocation",
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3 text-foreground">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2.5" />
                  {item}
                </li>
              ))}
            </ul>
            <p className="text-primary mt-8 font-medium">
              Enforcement actions are logged and immutable.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Why Licensing Exists */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#050505]" data-testid="why-licensing">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-8">
              Why Licensing Exists
            </p>
            <p className="text-lg text-muted-foreground mb-4">Licensing protects:</p>
            <ul className="space-y-3 mb-8">
              {[
                "The system",
                "Other operators",
                "Investors",
                "The authority itself",
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3 text-foreground">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2.5" />
                  {item}
                </li>
              ))}
            </ul>
            <p className="text-foreground font-medium">
              Without enforcement, authority erodes.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Closing */}
      <section className="py-24 md:py-32 px-6 md:px-12" data-testid="license-closing">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="font-serif text-2xl md:text-3xl font-light text-foreground leading-relaxed">
              Licensing is the mechanism that makes authority 
              <span className="text-primary"> durable and transferable.</span>
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LicensePage;
